import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import dayjs from 'dayjs';
import {
  CTA,
  CustomDatePicker,
  CustomInput,
  CustomSelect,
  Icon,
  Modal,
  Text,
} from '../..';
import { withMediaQueries } from '../../../../app/hoc/withMediaQueries';
import { Footer, ScrollableArea } from '../style';
import { MODAL_CLOSE } from '../../../../app/redux/actions';
import {
  getValues,
  isValidForm,
  newForm,
  newFormField,
} from '../../../../utils/form';
import { RegexpType, validator } from '../../../../utils/validator';
import { InputRow, Separator } from './style';
import InputLegend from '../../InputLegend';
import theme from '../../../theme';
import CustomTextarea from '../../CustomTextarea';
import {
  EVENT_ACCESS_OPTS,
  EVENT_REGISTRATION_OPTS,
  HOURS_OPTS,
} from '../../../../utils/labels';
import {
  SESSION_CREATE,
  SESSION_UPDATE,
} from '../../../../app/redux/actions/tutors';
import { timestampToDate } from '../../../../utils/common';
import {
  BBB_ENABLED_GET,
  BBB_URL_GET,
} from '../../../../app/redux/actions/events';

const initialForm = newForm([
  newFormField({
    field: 'title',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'date',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'start_time',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'end_time',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'attendance',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'access_type',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'available_places',
    required: true,
    type: RegexpType.NUMBER,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'url',
    required: false,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'address',
    required: false,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
  newFormField({
    field: 'description',
    required: true,
    type: RegexpType.STRING,
    valid: true,
    value: undefined,
  }),
]);

export const ModalCreateSessionId = 'ModalCreateSession';

const ModalCreateSession = ({
  mediaIsPhone,
  isOpen,
  id,
  payload,
  activities,
  bbbEnabled,
  bbbUrl,
  createSession,
  updateSession,
  modalClose,
  bbbEnabledGet,
  bbbUrlGet,
}) => {
  const scrollableAreaRef = useRef(null);
  const [form, setForm] = useState({ ...initialForm });
  const [isVirtual, setIsVirtual] = useState();
  const [startTime, setStartTime] = useState(HOURS_OPTS);
  const [endTime, setEndTime] = useState(HOURS_OPTS);

  useEffect(() => {
    if (isOpen && id === ModalCreateSessionId) {
      bbbEnabledGet();
      if (payload?.activityKey && payload?.sectionKey && activities) {
        if (
          activities?.sections &&
          activities.sections[payload?.sectionKey]?.activities &&
          activities.sections[payload?.sectionKey]?.activities[
            payload?.activityKey
          ]?.data
        ) {
          const currentvalues =
            activities.sections[payload?.sectionKey].activities[
              payload?.activityKey
            ]?.data;
          const editForm = { ...initialForm };
          Object.keys(editForm).forEach((k) => {
            if (editForm[k]) editForm[k].value = currentvalues[k] || '';
          });
          if (currentvalues.date) {
            editForm.date.value = dayjs(currentvalues.date)?.unix();
          }
          setForm({
            ...editForm,
          });
        }
      } else {
        const editForm = { ...initialForm };
        Object.keys(editForm).forEach((k) => (editForm[k].value = ''));
        setForm({ ...editForm });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    if (payload?.type === 'activity_session_virtual_room') {
      setIsVirtual(true);
      setForm({
        ...initialForm,
        url: {
          ...initialForm.url,
          valid: true,
        },
      });
    } else {
      setIsVirtual(false);
      setForm({
        ...initialForm,
        address: {
          ...initialForm.address,
          address: true,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    const newSessionForm = { ...form };
    if (form?.access_type?.value === 'free_access') {
      newSessionForm.available_places.required = false;
    } else newSessionForm.available_places.required = true;
    setForm(newSessionForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.access_type?.value]);

  const handleOnChange = (field = '', newValue) => {
    const { type } = form[field];
    setForm({
      ...form,
      [field]: {
        ...form[field],
        value: newValue,
        valid:
          typeof newValue === 'object' ? true : validator(type, newValue, true),
      },
    });
  };

  const getFormattedBody = () => {
    return {
      ...getValues(form),
      date: timestampToDate(form?.date?.value, 'YYYY-MM-DD'),
    };
  };

  useEffect(() => {
    if (bbbUrl) {
      handleOnChange('url', bbbUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbbUrl]);

  useEffect(() => {
    const stIndex = HOURS_OPTS.findIndex(
      (h) => h.label === form.start_time.value
    );
    const etIndex = HOURS_OPTS.findIndex(
      (h) => h.label === form.end_time.value
    );

    setStartTime(
      HOURS_OPTS.map((h, i) => ({
        ...h,
        isDisabled: etIndex > -1 ? i + 1 > etIndex : false,
      }))
    );
    setEndTime(
      HOURS_OPTS.map((h, i) => ({
        ...h,
        isDisabled: stIndex + 1 > i,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <Modal
      title={`Crea classe ${isVirtual ? 'virtuale' : 'in presenza'}`}
      modalId={ModalCreateSessionId}
      scrollableAreaRef={scrollableAreaRef}
      description="Inserisci di seguito tutti i valori per creare l'attività"
    >
      <ScrollableArea ref={scrollableAreaRef}>
        <InputRow>
          <div>
            <InputLegend
              label="Titolo attività"
              required
              htmlFor="activity-title"
            />
            <CustomInput
              size={mediaIsPhone ? 'full' : 'm'}
              placeholder="Inserisci"
              value={form.title.value}
              onChange={(v) => handleOnChange('title', v)}
              id="activity-title"
            />
          </div>
          <div>
            <InputLegend label="Data" required htmlFor="session-date" />
            <CustomDatePicker
              size={mediaIsPhone ? 'full' : 'm'}
              id="session-date"
              minDate={new Date()}
              widthInput={mediaIsPhone ? '100%' : '315px'}
              onChange={(date) => handleOnChange('date', dayjs(date)?.unix())}
              value={
                form?.date?.value ? new Date(1000 * form.date.value) : null
              }
            />
          </div>
          <div className="d-flex set-hours">
            <div>
              <InputLegend label="Ora inizio" required htmlFor="start-time" />
              <CustomSelect
                size={mediaIsPhone ? 'full' : 's'}
                id="start-time"
                options={startTime}
                value={form.start_time.value}
                onChange={(v) => handleOnChange('start_time', v.label)}
                selected={
                  startTime.find(
                    (opt) => opt.label === form.start_time.value
                  ) || null
                }
                isSearchable
              />
            </div>
            <Icon
              type="arrow-narrow-right"
              stroke={theme().colors.textPrimary}
            />
            <div>
              <InputLegend label="Ora fine" required htmlFor="end-time" />
              <CustomSelect
                size={mediaIsPhone ? 'full' : 's'}
                id="end-time"
                options={endTime}
                value={form.end_time.value}
                onChange={(v) => handleOnChange('end_time', v.label)}
                selected={
                  endTime.find((opt) => opt.label === form.end_time.value) ||
                  null
                }
                isSearchable
              />
            </div>
          </div>
        </InputRow>
        <Separator />
        <InputRow>
          <div>
            <InputLegend
              label="Tipologia accesso"
              required
              htmlFor="access-type"
            />
            <CustomSelect
              size={mediaIsPhone ? 'full' : 'm'}
              id="access-type"
              value={form.access_type.value}
              options={EVENT_ACCESS_OPTS}
              onChange={({ value }) => {
                const updatedForm = { ...form };
                updatedForm.access_type.value = value;
                if (value === 'free_access') {
                  updatedForm.attendance.value = 'without_attendance';
                }
                setForm(updatedForm);
              }}
              selected={
                EVENT_ACCESS_OPTS?.find(
                  (opt) => opt.value === form?.access_type?.value
                ) || null
              }
            />
          </div>
          <div>
            <InputLegend
              label="Registrazione presenze"
              tooltipKey="presence_registration"
              required
              htmlFor="presence-registration"
            />
            <CustomSelect
              size={mediaIsPhone ? 'full' : 'm'}
              id="presence-registration"
              value={form.attendance.value}
              options={EVENT_REGISTRATION_OPTS}
              onChange={({ value }) => handleOnChange('attendance', value)}
              selected={
                EVENT_REGISTRATION_OPTS?.find(
                  (opt) => opt.value === form?.attendance?.value
                ) || null
              }
              disabled={form.access_type.value === 'free_access'}
            />
          </div>
          {form?.access_type?.value === 'limited_access' && (
            <div>
              <InputLegend
                label="Posti disponibili"
                tooltipKey="available_places"
                required
                htmlFor="available-places"
              />
              <CustomInput
                type="number"
                id="available-places"
                placeholder="Inserisci"
                size={mediaIsPhone ? 'full' : 'm'}
                value={form.available_places.value}
                onChange={(v) => handleOnChange('available_places', v)}
              />
            </div>
          )}
        </InputRow>
        <Separator />
        {isVirtual ? (
          <InputRow className="btns-wrapper">
            <div>
              <InputLegend
                label="Link di accesso"
                required
                htmlFor="access-link"
              />
              <CustomInput
                size={mediaIsPhone ? 'full' : 'l'}
                id="access-link"
                placeholder="Link di accesso"
                value={form.url?.value || ''}
                onChange={(v) => handleOnChange('url', v)}
              />
            </div>
            {bbbEnabled && (
              <>
                <CTA
                  fullsize={mediaIsPhone}
                  btnText="Genera URL BigBlueButton"
                  onClick={() => bbbUrlGet(form.title.value)}
                  disabled={!form.title.value}
                />
                <CTA
                  fullsize={mediaIsPhone}
                  btnText="Reset URL"
                  type="secondary"
                  onClick={(v) => handleOnChange('url', '')}
                />
              </>
            )}
          </InputRow>
        ) : (
          <div>
            <InputLegend
              label="Luogo"
              tooltipKey="event_location"
              required
              htmlFor="event-location"
            />
            <CustomInput
              size={mediaIsPhone ? 'full' : 'l'}
              id="event-location"
              placeholder="Inserisci"
              value={form.address?.value || ''}
              onChange={(v) => handleOnChange('address', v)}
            />
          </div>
        )}
        <Separator />
        <div>
          <InputLegend
            label="Descrizione"
            required
            htmlFor="session-description"
          />
          <CustomTextarea
            placeholder="Inserisci"
            id="session-description"
            value={form.description.value}
            onChange={(value) => handleOnChange('description', value)}
            minHeight={100}
            mb={48}
          />
        </div>
        <Text type="b3" mb={48} color={theme().colors.fail}>
          * campo obbligatorio
        </Text>
      </ScrollableArea>
      <Footer>
        <CTA
          type="secondary"
          btnText="annulla"
          fullsize={mediaIsPhone}
          onClick={modalClose}
        />
        {payload?.copyModel ? (
          <CTA
            type="primary"
            btnText="Crea attività"
            fullsize={mediaIsPhone}
            disabled={!isValidForm(form)}
            onClick={() => {
              // combines ADD_ACTIVITY & UPDATE_ACTIVITY (creates actId in redux store)
              createSession({
                body: getFormattedBody(),
                secId: payload?.sectionKey,
                activityType: payload?.type,
                field: 'data',
                isCopy: true,
              });
              modalClose();
            }}
          />
        ) : payload?.sessionId ? (
          <CTA
            type="primary"
            btnText="Aggiorna"
            fullsize={mediaIsPhone}
            disabled={!isValidForm(form)}
            onClick={() => {
              // makes only UPDATE_ACTIVITY (actId already exists)
              updateSession({
                body: getFormattedBody(),
                secId: payload?.sectionKey,
                actId: payload?.activityKey,
                field: 'data',
                sessionId: payload?.sessionId,
              });
              modalClose();
            }}
          />
        ) : (
          <CTA
            type="primary"
            btnText="conferma"
            fullsize={mediaIsPhone}
            disabled={!isValidForm(form)}
            onClick={() => {
              // makes only UPDATE_ACTIVITY (actId already exists)
              updateSession({
                body: getFormattedBody(),
                secId: payload?.sectionKey,
                actId: payload?.activityKey,
                field: 'data',
              });
              modalClose();
            }}
          />
        )}
      </Footer>
    </Modal>
  );
};

ModalCreateSession.propTypes = {
  //HOC: withMediaQueries
  mediaIsPhone: PropTypes.bool,
  // state
  payload: PropTypes.object,
  isOpen: PropTypes.bool,
  id: PropTypes.string,
  activities: PropTypes.object,
  bbbEnabled: PropTypes.bool,
  bbbUrl: PropTypes.string,
  // dispatch
  modalClose: PropTypes.func.isRequired,
  createSession: PropTypes.func.isRequired,
  updateSession: PropTypes.func.isRequired,
  bbbEnabledGet: PropTypes.func.isRequired,
  bbbUrlGet: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const { payload, isOpen, id } = state.app.modal;
      const { activities } = state.tutors;
      const { enabled: bbbEnabled, link: bbbUrl } = state.events.bbb;
      return { payload, isOpen, id, activities, bbbEnabled, bbbUrl };
    },
    (dispatch) => ({
      modalClose: () => dispatch({ type: MODAL_CLOSE }),
      createSession: ({ body, secId, activityType, field, isCopy }) =>
        dispatch({
          type: SESSION_CREATE._REQUEST,
          body,
          secId,
          activityType,
          field,
          isCopy,
        }),
      updateSession: ({ body, secId, actId, field, sessionId }) =>
        dispatch({
          type: SESSION_UPDATE._REQUEST,
          body,
          secId,
          actId,
          field,
          sessionId,
        }),
      bbbEnabledGet: () => dispatch({ type: BBB_ENABLED_GET._REQUEST }),
      bbbUrlGet: (title) => dispatch({ type: BBB_URL_GET._REQUEST, title }),
    })
  ),
  withMediaQueries
)(ModalCreateSession);
